import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { services, siteInfo } from '../data/mock';
import { Link } from 'react-router-dom';
import { Flower2, Sparkles, Heart, Globe, ArrowRight, Check } from 'lucide-react';

const iconMap = {
  Flower2: Flower2,
  Sparkles: Sparkles,
  Heart: Heart,
  Globe: Globe,
};

const ServicesPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const serviceDetails = [
    {
      ...services[0],
      features: [
        "Consultation personnalisée",
        "Choix des fleurs et couleurs",
        "Composition sur mesure",
        "Livraison soignée"
      ],
      image: "https://images.unsplash.com/photo-1512995738062-2d4ab94caf74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxleG90aWMlMjBmbG93ZXJzfGVufDB8fHx8MTc2OTMxMTkxN3ww&ixlib=rb-4.1.0&q=85"
    },
    {
      ...services[1],
      features: [
        "Analyse de l'espace",
        "Concept floral unique",
        "Installation professionnelle",
        "Coordination le jour J"
      ],
      image: "https://images.unsplash.com/photo-1560313793-2f4383450a3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHx0cm9waWNhbCUyMGZsb3dlcnN8ZW58MHx8fHwxNzY5MzExOTEyfDA&ixlib=rb-4.1.0&q=85"
    },
    {
      ...services[2],
      features: [
        "Bouquet de mariée",
        "Décoration de cérémonie",
        "Centres de table",
        "Accessoires floraux"
      ],
      image: "https://images.unsplash.com/photo-1604545199306-23ee04a34dd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGZsb3dlcnN8ZW58MHx8fHwxNzY5MzExOTEyfDA&ixlib=rb-4.1.0&q=85"
    },
    {
      ...services[3],
      features: [
        "Livraison mondiale",
        "Suivi en temps réel",
        "Emballage premium",
        "Service client dédié"
      ],
      image: "https://images.pexels.com/photos/18687551/pexels-photo-18687551.jpeg"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#E8A5C4]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C45B9F]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center max-w-3xl mx-auto transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Ce que nous offrons
            </span>
            <h1 className="font-heading text-5xl lg:text-6xl text-[#3D2E35] mt-4">
              Nos <span className="font-script text-[#8B4B7C]">Services</span>
            </h1>
            <p className="text-lg text-[#6B5A62] mt-6">
              Des prestations sur mesure pour sublimer vos moments les plus précieux
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {serviceDetails.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Flower2;
            const isEven = index % 2 === 0;
            
            return (
              <div
                key={service.id}
                className={`mb-24 last:mb-0 transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                  {/* Image */}
                  <div className={`relative ${isEven ? 'order-1' : 'order-1 lg:order-2'}`}>
                    <div className="relative">
                      <div className="overflow-hidden rounded-[2rem]">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-[400px] object-cover"
                        />
                      </div>
                      <div className={`absolute -bottom-4 ${isEven ? '-right-4' : '-left-4'} w-full h-full border-2 border-[#C45B9F] rounded-[2rem] -z-10`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`space-y-6 ${isEven ? 'order-2' : 'order-2 lg:order-1'}`}>
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E8A5C4] to-[#C45B9F] flex items-center justify-center">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h2 className="font-heading text-3xl lg:text-4xl text-[#3D2E35]">
                      {service.title}
                    </h2>
                    <p className="text-[#6B5A62] leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center space-x-3">
                          <div className="w-6 h-6 rounded-full bg-[#E8A5C4]/30 flex items-center justify-center">
                            <Check size={14} className="text-[#8B4B7C]" />
                          </div>
                          <span className="text-[#6B5A62]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-[#8B4B7C] hover:text-[#C45B9F] font-medium transition-colors duration-300 group"
                    >
                      Demander un devis
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#8B4B7C] to-[#C45B9F] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-heading text-4xl lg:text-5xl text-white mb-6">
            Prêt à créer quelque chose de magique ?
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Contactez-nous pour discuter de votre projet et laissez-nous transformer vos rêves floraux en réalité.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 rounded-full bg-white text-[#8B4B7C] font-medium hover:bg-[#FAF8F5] transition-colors duration-300"
            >
              Nous contacter
              <ArrowRight className="ml-2" size={20} />
            </Link>
            <a
              href={`tel:${siteInfo.phone}`}
              className="inline-flex items-center px-8 py-4 rounded-full border-2 border-white text-white font-medium hover:bg-white/10 transition-colors duration-300"
            >
              {siteInfo.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
